import { BrowserWindowEvent } from 'pf-frontend-common/src/module/browser/window.event';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { EventEmitterAwareInterface } from 'pf-frontend-common/src/module/event/emitter-aware.interface';
import { JsonApiModelInterface } from 'pf-frontend-common/src/module/json-api/model.interface';
import { ModalServiceInterface } from 'pf-frontend-common/src/service/modal/service.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { KeyboardCharcodeEnum } from 'common/module/keyboard/charcode.enum';
import { LeadEmailFormEvent } from 'common/module/lead/email/form.event';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { LeadEmailFormStrategyInterface } from 'common/module/lead/email/form/strategy.interface';
import { LeadEmailFormViewStore } from 'common/module/lead/email/form/view-store';
import { LeadEmailFormViewStoreEvent } from 'common/module/lead/email/form/view-store.event';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';

import { LeadEmailFormOptionsInterface } from './form-options.interface';

/**
 * @deprecated TODO-FE[later] So basically this is a dynamic view but not really because it doesn't have an element...
 */
export class LeadEmailForm implements EventEmitterAwareInterface {
  /**
   * View dynamic (set on runtime)
   */
  private viewAdapter: ViewDynamicInterface<LeadEmailFormViewStore>;

  /**
   * Is the form open or closed ?
   */
  private isOpen: boolean;

  /**
   * Constructor
   */
  constructor(
    private modalService: ModalServiceInterface,
    private eventEmitter: EventEmitterInterface,
    private windowService: WindowServiceInterface
  ) {}

  /**
   * Initialize form
   */
  public initialize(options?: LeadEmailFormOptionsInterface): void {
    this.getViewAdapter().initialize({
      element: this.modalService.getContentElement(),
    });

    this.getViewAdapter()
      .getViewStore()
      .initialize({
        model: options && options.model,
      });

    // Attach observers
    this.observers();
  }

  /**
   * Sets the view adapter
   */
  public setViewAdapter(viewAdapter: ViewDynamicInterface<LeadEmailFormViewStore>): void {
    this.viewAdapter = viewAdapter;
  }

  /**
   * Returns the view adapter
   */
  public getViewAdapter(): ViewDynamicInterface<LeadEmailFormViewStore> {
    if (!this.viewAdapter) {
      throw new Error('LeadEmailForm.viewAdapter has not been set before calling getViewAdapter()');
    }

    return this.viewAdapter;
  }

  /**
   * Sets strategy to use
   */
  public setFormStrategy(strategy: LeadEmailFormStrategyInterface): void {
    this.getViewAdapter().getViewStore().setStrategy(strategy);
  }

  /**
   * Get form strategy
   */
  public getFormStrategy(): LeadEmailFormStrategyInterface {
    return this.getViewAdapter().getViewStore().getStrategy();
  }

  /**
   * @inheritDoc
   */
  public initState(): void {
    this.getViewAdapter().getViewStore().initState();
  }

  /**
   * @inheritDoc
   */
  public updateModel(model: JsonApiModelInterface): void {
    this.getViewAdapter().getViewStore().updateModel(model);
  }

  /**
   * @inheritDoc
   */
  public getModel(): JsonApiModelInterface {
    return this.getViewAdapter().getViewStore().getModel();
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    // Open modal
    this.setOpen(true);
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.getViewAdapter()
      .getViewStore()
      .getEventEmitter()
      .addListener(LeadEmailFormViewStoreEvent.submit, this.onSubmit);

    this.getViewAdapter().getViewStore().getEventEmitter().addListener(LeadEmailFormViewStoreEvent.close, this.onClose);

    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.keyup, this.onKeyupWindow);
  }

  /**
   * Set the open state of the form
   */
  private setOpen(isOpen: boolean): void {
    this.isOpen = isOpen;

    if (isOpen) {
      this.getViewAdapter().render();

      this.modalService.open();
    } else {
      this.modalService.close();

      // Empty the modal
      this.modalService.setContentView(null);
    }
  }

  /**
   * Submitted form
   */
  private onSubmit = (input: LeadEmailFormInputInterface): void => {
    this.getEventEmitter().emit(LeadEmailFormEvent.submit, input);
  };

  /**
   * Closed form
   */
  private onClose = (): void => {
    this.setOpen(false);
  };

  /**
   * Fired document keyup event
   */
  private onKeyupWindow = (e: KeyboardEvent) => {
    if (this.isOpen && e.keyCode === KeyboardCharcodeEnum.escape) {
      this.setOpen(false);
    }
  };
}
